import { useAtomValue } from 'jotai'
import { LucideCopy, LucideShare } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  EmailIcon,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  XIcon,
} from 'react-share'
import { aOrganization } from '~/atoms'
import { Button, Popover, toast } from '~/components/ui'
import { useEmbed, useMapListingLink } from '~/hooks'
import { type Listing } from '~/models'

export default function ListingPublicShare({
  listing,
  className,
}: {
  listing: Listing
  className?: string
}) {
  const organization = useAtomValue(aOrganization)
  const { hostURL, isEmbed } = useEmbed()
  const [isOpen, setIsOpen] = useState(false)

  const listingLink = useMapListingLink(listing)

  const shareUrl = useMemo(() => {
    if (isEmbed && hostURL) {
      try {
        const url = new URL(hostURL)
        url.hash = `/listings/${listing.slug}`

        return url.toString()
      } catch {
        return undefined
      }
    } else if (listingLink.includes('http')) {
      return listingLink
    } else if (typeof window !== 'undefined') {
      return window.location.href
    }
  }, [listingLink, isEmbed, hostURL])

  return (
    <div className={className}>
      <Popover open={isOpen} onOpenChange={(value) => setIsOpen(value)}>
        <div className="flex justify-items-end">
          <Popover.Trigger asChild>
            <Button variant="link" className="h-auto px-0 py-0 text-base">
              <LucideShare className="mr-2 h-5 w-5" /> Share Listing
            </Button>
          </Popover.Trigger>
          <Popover.Content>
            <h3 className="text-lg">Share this listing</h3>
            {shareUrl && (
              <div className="my-4 flex justify-evenly">
                <a
                  href={`mailto:?subject=${listing.name}&body=Check out this listing on ${organization!.name}: ${shareUrl}`}
                  target="_blank">
                  <EmailIcon size={36} borderRadius={10}></EmailIcon>
                </a>
                <FacebookShareButton url={shareUrl}>
                  <FacebookIcon size={36} borderRadius={10}></FacebookIcon>
                </FacebookShareButton>
                <TwitterShareButton url={shareUrl}>
                  <XIcon size={36} borderRadius={10}></XIcon>
                </TwitterShareButton>
                <LinkedinShareButton url={shareUrl}>
                  <LinkedinIcon size={36} borderRadius={10}></LinkedinIcon>
                </LinkedinShareButton>
              </div>
            )}
            {!isEmbed && (
              <div className="w-full">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center rounded-md"
                  onClick={async (e) => {
                    e.stopPropagation()
                    setIsOpen(false)
                    try {
                      await navigator.clipboard.writeText(shareUrl!)
                      toast({
                        title: 'Link Copied',
                        description:
                          'The public listing link has been copied to your clipboard.',
                      })
                    } catch (error) {
                      console.error('Failed to ', error)
                      toast({
                        variant: 'destructive',
                        title: 'Error Copying Link',
                        description:
                          'There was a problem copying the link to your clipboard.',
                      })
                    }
                  }}>
                  <LucideCopy className="mr-2 h-4 w-4" />
                  Copy Link
                </Button>
              </div>
            )}
          </Popover.Content>
        </div>
      </Popover>
    </div>
  )
}
